import React, { useState, useMemo } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  ArrowUpDown,
  DollarSign,
  CreditCard,
  Users,
  Gauge,
  Layers,
  HelpCircle,
  Trash2,
  MoreVertical,
  Edit,
  PenLine
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Define the metric type for our tables
// This interface should match the one in types/metricTypes.ts
export interface Metric {
  id: string;
  display_name: string;  // Changed to match backend naming
  name?: string;
  modality?: string;
  sampleValues?: string[];
  sample_values?: string[];
  samplePrices?: (string | number)[];
  quarter?: string;
  versions?: any[];
  associatedPlans?: any[];
}

// Helper function to get the appropriate icon based on modality
export const getModalityIcon = (modality: string) => {
  switch (modality?.toLowerCase()) {
    case 'usage':
      return <Gauge className="h-4 w-4 text-blue-500" />;
    case 'license':
      return <Users className="h-4 w-4 text-green-500" />;
    case 'credit':
      return <CreditCard className="h-4 w-4 text-purple-500" />;
    case 'threshold':
      return <Layers className="h-4 w-4 text-orange-500" />;
    case 'flat':
      return <DollarSign className="h-4 w-4 text-gray-600" />;
    default:
      return <HelpCircle className="h-4 w-4 text-gray-500" />;
  }
};

// Helper function to get the appropriate background color based on modality
export const getModalityColor = (modality: string) => {
  switch (modality?.toLowerCase()) {
    case 'usage':
      return 'bg-blue-100';
    case 'license':
      return 'bg-green-100';
    case 'credit':
      return 'bg-purple-100';
    case 'threshold':
      return 'bg-orange-100';
    case 'flat':
      return 'bg-gray-50';
    default:
      return 'bg-gray-100';
  }
};

// Helper function to get the appropriate text color based on modality
export const getModalityTextColor = (modality: string) => {
  switch (modality?.toLowerCase()) {
    case 'usage':
      return 'text-blue-700';
    case 'license':
      return 'text-green-700';
    case 'credit':
      return 'text-purple-700';
    case 'threshold':
      return 'text-orange-700';
    case 'flat':
      return 'text-gray-600';
    default:
      return 'text-gray-700';
  }
};

interface MetricsTableProps {
  metrics: Metric[];
  title?: string;
  description?: string;
  onDeleteMetric?: (metric: Metric) => void;
  onEditMetric?: (metric: Metric) => void;
  // Add alias props for simpler API
  onDelete?: (metric: Metric) => void;
  onEdit?: (metric: Metric) => void;
  showSampleValues?: boolean;
}

const MetricsTable: React.FC<MetricsTableProps> = ({ 
  metrics, 
  title = '', 
  description = '', 
  onDeleteMetric,
  onEditMetric,
  onDelete,
  onEdit,
  showSampleValues = false
}) => {
  // Use provided handlers or their aliases
  const handleDelete = onDeleteMetric || onDelete;
  const handleEdit = onEditMetric || onEdit;
  const [sortColumn, setSortColumn] = useState<string>('associatedPlans');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [showAll, setShowAll] = useState<boolean>(false);

  // Limit the number of rows shown initially to prevent the page from becoming too long
  const MAX_ROWS = 5;

  // Toggle sort when clicking a column header
  const toggleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  // Sort metrics based on current sort column and direction
  const sortedMetrics = useMemo(() => {
    return [...metrics].sort((a, b) => {
      let comparison = 0;
      if (sortColumn === 'display_name') {
        comparison = a.display_name.localeCompare(b.display_name);
      } else if (sortColumn === 'modality') {
        comparison = a.modality.localeCompare(b.modality);
      } else if (sortColumn === 'sampleValues') {
        const aValue = a.sampleValues && a.sampleValues.length > 0 ? a.sampleValues[0] : '';
        const bValue = b.sampleValues && b.sampleValues.length > 0 ? b.sampleValues[0] : '';
        comparison = aValue.localeCompare(bValue);
      } else if (sortColumn === 'associatedPlans') {
        const aCount = a.associatedPlans ? a.associatedPlans.length : 0;
        const bCount = b.associatedPlans ? b.associatedPlans.length : 0;
        comparison = bCount - aCount;
      }
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [metrics, sortColumn, sortDirection]);

  if (metrics.length === 0) {
    return null;
  }

  return (
    <div className="mb-8">
      <h2 className="text-lg font-semibold mb-2">{title}</h2>
      <p className="text-sm text-gray-500 mb-4">{description}</p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-1/4">
              <Button
                variant="ghost"
                onClick={() => toggleSort('modality')}
                className="flex items-center gap-1 p-0 h-auto font-medium"
              >
                Modality
                <ArrowUpDown className="h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead className="w-1/3">
              <Button
                variant="ghost"
                onClick={() => toggleSort('display_name')}
                className="flex items-center gap-1 p-0 h-auto font-medium"
              >
                Name
                <ArrowUpDown className="h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                onClick={() => toggleSort('sampleValues')}
                className="flex items-center gap-1 p-0 h-auto font-medium"
              >
                Sample Values
                <ArrowUpDown className="h-4 w-4" />
              </Button>
            </TableHead>
            <TableHead>
              <Button
                variant="ghost"
                onClick={() => toggleSort('associatedPlans')}
                className="flex items-center gap-1 p-0 h-auto font-medium"
              >
                Plans
                <ArrowUpDown className="h-4 w-4" />
              </Button>
            </TableHead>
            {(onDeleteMetric || onEditMetric) && (
              <TableHead className="w-[80px] text-right">
                <span className="sr-only">Actions</span>
              </TableHead>
            )}
          </TableRow>
        </TableHeader>
        <TableBody>
          {(showAll ? sortedMetrics : (sortedMetrics || []).slice(0, MAX_ROWS)).map((metric) => (
            <TableRow key={metric.id}>
              <TableCell>
                <div className="flex items-center gap-2">
                  <div className={`p-1.5 rounded-md ${getModalityColor(metric.modality)}`}>
                    {getModalityIcon(metric.modality)}
                  </div>
                  <span className={`capitalize ${getModalityTextColor(metric.modality)}`}>
                    {metric.modality}
                  </span>
                </div>
              </TableCell>
              <TableCell className="font-medium">
                {metric.display_name || "Unnamed Metric"}
                {metric.associatedPlans && metric.associatedPlans.length > 0 && (
                  <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                    {metric.associatedPlans?.length} {metric.associatedPlans?.length === 1 ? 'plan' : 'plans'}
                  </span>
                )}
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {(metric.sampleValues || []).slice(0, 2).map((value, i) => (
                    <Badge key={i} variant="secondary" className="text-xs whitespace-normal">
                      {value}
                    </Badge>
                  ))}
                  {(metric.sampleValues || []).length > 2 && (
                    <Badge variant="outline" className="text-xs">
                      +{(metric.sampleValues || []).length - 2} more
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {metric.associatedPlans && metric.associatedPlans.length > 0 ? (
                    <>
                      {/* Show first 3 plans and a count badge if there are more */}
                      {(metric.associatedPlans || []).length > 0 && (metric.associatedPlans || []).slice(0, 3).map((plan, idx) => (
                        <TooltipProvider key={plan.id}>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge
                                variant="outline"
                                className={`text-xs ${plan.bgColor || 'bg-blue-50'} ${plan.textColor || 'text-blue-700'} ${plan.borderColor || 'border-blue-200'} cursor-help whitespace-nowrap`}
                              >
                                {plan.displayName || plan.name || plan.id}
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="text-xs space-y-1">
                                <p><strong>Plan:</strong> {plan.displayName || plan.name || plan.id}</p>
                                <p><strong>Metric:</strong> {metric.display_name} ({metric.modality})</p>
                                {(plan.price !== undefined && plan.price !== null) && (
                                  <p><strong>Price:</strong> {plan.price}{plan.unit ? ` per ${plan.unit}` : ''}</p>
                                )}
                                {(metric.samplePrices && metric.samplePrices.length > 0) && (
                                  <p><strong>Sample Price:</strong> {metric.samplePrices[0]}</p>
                                )}
                                <div className="mt-2 pt-2 border-t border-gray-200">
                                  <p className="text-gray-400 text-[10px]">Plan ID: {plan.id}</p>
                                  <p className="text-gray-400 text-[10px]">Metric ID: {metric.id}</p>
                                  <p className="text-gray-400 text-[10px]">Source: {plan.source || "Direct association"}</p>
                                </div>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ))}
                      {/* Show count badge if there are more than 3 plans */}
                      {(metric.associatedPlans || []).length > 3 && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Badge variant="outline" className="text-xs bg-gray-100 text-gray-700 border-gray-200">
                                +{(metric.associatedPlans || []).length - 3} more
                              </Badge>
                            </TooltipTrigger>
                            <TooltipContent>
                              <div className="text-xs">
                                <p className="font-medium mb-1">All associated plans:</p>
                                <ul className="space-y-1">
                                  {(metric.associatedPlans || [])
                                    .sort((a, b) => (a.displayName || a.name || '').localeCompare(b.displayName || b.name || ''))
                                    .map(plan => (
                                      <li key={plan.id} className="flex items-center">
                                        <span className="h-2 w-2 rounded-full bg-blue-400 mr-1.5"></span>
                                        {plan.displayName || plan.name || plan.id}
                                      </li>
                                    ))}
                                </ul>
                              </div>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </>
                  ) : (
                    <span className="text-xs text-gray-400">No plans</span>
                  )}
                </div>
              </TableCell>
              {(handleDelete || handleEdit) && (
                <TableCell className="w-[80px] text-right p-0 pr-2">
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-8 w-8"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <MoreVertical className="h-4 w-4 text-gray-500" />
                        <span className="sr-only">Open menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="w-[160px]">
                      {handleEdit && (
                        <DropdownMenuItem
                          className="focus:bg-blue-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(metric);
                          }}
                        >
                          <PenLine className="h-4 w-4 mr-2 text-blue-600" />
                          <span>Edit Metric</span>
                        </DropdownMenuItem>
                      )}
                      {handleDelete && (
                        <DropdownMenuItem
                          className="text-red-600 focus:text-red-600 focus:bg-red-50"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDelete(metric);
                          }}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          <span>Delete Metric</span>
                        </DropdownMenuItem>
                      )}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              )}
            </TableRow>
          ))}
          {sortedMetrics.length > MAX_ROWS && (
            <TableRow>
              <TableCell colSpan={handleDelete || handleEdit ? 5 : 4} className="text-center p-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowAll(!showAll)}
                  className="text-xs"
                >
                  {showAll ? "Show Less" : `Show ${sortedMetrics.length - MAX_ROWS} More Metrics`}
                </Button>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
};

export default MetricsTable;
